import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './ProductSections.css';
import ProductCard from './ProductCard';
import { useData } from '../context/DataContext';
import { api } from '../utils/api';
import { groupBrowsableProductsForGrid } from '../utils/productOffers';
import {
  shapeProductForStorefront,
  filterBrowsableProducts,
  buildBrowsableProductIdSet,
  isProductOpenableInCatalog,
} from '../utils/catalogProductShape';

const SECTION_PRODUCTS_REFRESH_MS = 5 * 60 * 1000;

function extractProductsFromApiResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.products && Array.isArray(payload.products)) return payload.products;
  if (payload?.data?.products && Array.isArray(payload.data.products)) return payload.data.products;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  return [];
}

function sectionKeyToApiParam(backendKey) {
  if (backendKey === 'bestSellers') return 'bestsellers';
  return backendKey;
}

/** Match `.section-products` grid: minmax(220px) / 20px gap; 160px / 15px below 768px. */
function computeSectionGridColumns(windowWidth) {
  const isMobile = windowWidth <= 768;
  const minCard = isMobile ? 160 : 220;
  const gap = isMobile ? 15 : 20;
  const horizontalPadding = 40; // .section-container horizontal padding
  const maxInner = 1400 - horizontalPadding;
  const available = Math.min(maxInner, Math.max(minCard, windowWidth - horizontalPadding));
  return Math.max(1, Math.floor((available + gap) / (minCard + gap)));
}

const SECTION_COLUMNS_DEFAULT = 4;

function clampSectionColumnsPerRow(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return SECTION_COLUMNS_DEFAULT;
  return Math.min(5, Math.max(3, Math.round(n)));
}

function getColumnsForSection(section, windowWidth, sectionsConfig) {
  if (windowWidth <= 768) {
    return computeSectionGridColumns(windowWidth);
  }
  const configured = sectionsConfig[section.backendKey]?.columnsPerRow;
  if (configured == null) {
    return clampSectionColumnsPerRow(computeSectionGridColumns(windowWidth));
  }
  return clampSectionColumnsPerRow(configured);
}

function sectionUsesFixedColumns(section, windowWidth, sectionsConfig) {
  return (
    windowWidth > 768 &&
    sectionsConfig[section.backendKey]?.columnsPerRow != null
  );
}

/** Default visible cards for a section: the admin "display count" setting. */
function defaultVisibleCount(section, sectionsConfig, totalProducts) {
  if (totalProducts <= 0) return 0;
  const fromConfig = sectionsConfig?.[section?.backendKey]?.displayCount;
  const configured = Number(fromConfig ?? section?.displayCount);
  const base = Number.isFinite(configured) && configured > 0 ? Math.round(configured) : 6;
  return Math.min(totalProducts, base);
}

const sectionProductImages = {
  electronics: '/images/products/electronics.jpg',
  fashion: '/images/products/fashion.jpg',
  home: '/images/products/home.jpg',
  sports: '/images/products/sports.jpg',
  beauty: '/images/products/beauty.jpg',
  toys: '/images/products/toys.jpg'
};

const getSectionProductImage = (category) => sectionProductImages[category] || sectionProductImages.electronics;

/** Sentinel that triggers onLoadMore when scrolled near, for View All infinite loading. */
const SectionInfiniteLoader = ({ hasMore, loading, onLoadMore }) => {
  const ref = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!hasMore) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMoreRef.current();
        }
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore]);

  if (!hasMore) return null;
  return (
    <div className="section-infinite-loader" ref={ref} aria-live="polite">
      {loading && (
        <>
          <span className="section-infinite-spinner" aria-hidden="true" />
          <span>Loading more products…</span>
        </>
      )}
    </div>
  );
};

// Map backend section keys to frontend section IDs
const sectionKeyMap = {
  bestSellers: 'bestsellers',
  weeklyPromotion: 'weekly',
  popular: 'popular'
};

const ProductSections = ({ onCategorySelect, onAddToList, onAddToCart, onAddToCompare, onCheckAvailability, onRemoveFromList, listProductIds, compareProductIds }) => {
  const handleAddToList = onAddToList || onAddToCart;
  const { products, lastFetch } = useData();
  const [sectionsConfig, setSectionsConfig] = useState({
    bestSellers: { title: 'Best Sellers', isActive: true, displayCount: 6, columnsPerRow: 4 },
    weeklyPromotion: { title: 'Weekly Promotion', isActive: true, displayCount: 6, columnsPerRow: 4 },
    popular: { title: 'Populars', isActive: true, displayCount: 6, columnsPerRow: 4 }
  });
  const [displayCounts, setDisplayCounts] = useState({});
  const [expandedSections, setExpandedSections] = useState(() => new Set());
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1400
  );
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [sectionProductsByKey, setSectionProductsByKey] = useState({});
  const [sectionProductsLoading, setSectionProductsLoading] = useState(true);
  const sectionProductsInflightRef = useRef(null);
  const lastSectionProductsRefreshRef = useRef(0);
  const configLoadedOnceRef = useRef(false);
  // Per backendKey: { active, page, hasMore, loadingMore } for View All mode
  const [viewAllState, setViewAllState] = useState({});
  const viewAllStateRef = useRef(viewAllState);
  useEffect(() => {
    viewAllStateRef.current = viewAllState;
  }, [viewAllState]);

  // Fetch product sections configuration from API
  useEffect(() => {
    fetchSectionsConfig({ silent: false });
  }, []);

  // Auto-refresh sections config every 5 minutes (no skeleton flash)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSectionsConfig({ silent: true });
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Refresh when page becomes visible (silent)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSectionsConfig({ silent: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const sectionsConfigRef = useRef(sectionsConfig);
  sectionsConfigRef.current = sectionsConfig;

  const fetchSectionsConfig = async ({ silent = false } = {}) => {
    try {
      if (!silent && !configLoadedOnceRef.current) {
        setSectionsLoading(true);
      }
      const response = await api.get('/content/product-sections');
      
      let config = {};
      if (response.data) {
        config = response.data;
      } else if (typeof response === 'object') {
        config = response;
      }

      if (Object.keys(config).length > 0) {
        const prev = sectionsConfigRef.current;
        const merged = { ...prev, ...config };
        const layoutKeys = ['bestSellers', 'weeklyPromotion', 'popular', 'allProducts'];
        const layoutChanged = layoutKeys.some((key) => {
          const before = prev[key] || {};
          const after = merged[key] || {};
          return (
            before.displayCount !== after.displayCount ||
            before.columnsPerRow !== after.columnsPerRow ||
            before.isActive !== after.isActive
          );
        });
        setSectionsConfig(merged);
        if (layoutChanged) {
          setDisplayCounts({});
          setExpandedSections(new Set());
          setViewAllState({});
        }
      }
      configLoadedOnceRef.current = true;
    } catch (error) {
      console.error('Error fetching product sections config:', error);
      configLoadedOnceRef.current = true;
    } finally {
      setSectionsLoading(false);
    }
  };

  const sections = useMemo(
    () =>
      Object.keys(sectionsConfig)
        .filter(key => {
          if (key === 'allProducts') return false;
          const config = sectionsConfig[key];
          return config && config.isActive !== false;
        })
        .map(key => {
          const config = sectionsConfig[key];
          const frontendId = sectionKeyMap[key] || key.toLowerCase();
          return {
            backendKey: key,
            id: frontendId,
            title: config.title || key,
            displayCount: config.displayCount ?? 6,
            columnsPerRow:
              config.columnsPerRow != null
                ? clampSectionColumnsPerRow(config.columnsPerRow)
                : undefined,
            isActive: config.isActive !== false
          };
        }),
    [sectionsConfig]
  );

  const shapeSectionProductRows = useCallback((raw = []) => {
    const shaped = raw.map((p) => {
      try {
        return shapeProductForStorefront(p);
      } catch {
        const idVal = p._id || p.id;
        return { ...p, id: idVal, _id: idVal };
      }
    });
    return filterBrowsableProducts(
      shaped.map((p) => {
        let thumbnail = p.thumbnail;
        if (!thumbnail && p.images?.length) {
          const primaryIndex =
            p.primaryImageIndex != null && p.primaryImageIndex < p.images.length
              ? p.primaryImageIndex
              : 0;
          thumbnail = p.images[primaryIndex];
        }
        if (!thumbnail) {
          thumbnail = p.image || getSectionProductImage(p.categorySlug || p.category);
        }
        return { ...p, thumbnail, image: thumbnail };
      })
    );
  }, []);

  const sectionFetchLimit = useCallback(
    (section) =>
      Math.max(
        section.displayCount || 6,
        (sectionsConfig[section.backendKey]?.displayCount || 6) * 2,
        24
      ),
    [sectionsConfig]
  );

  const loadSectionProducts = useCallback(
    async ({ silent = false, force = false } = {}) => {
      // Use default/current section config immediately — do not wait on config API.
      if (sections.length === 0) return;

      const now = Date.now();
      if (
        !force &&
        silent &&
        now - lastSectionProductsRefreshRef.current < 15_000
      ) {
        return;
      }

      if (sectionProductsInflightRef.current) {
        return sectionProductsInflightRef.current;
      }

      const run = (async () => {
        if (!silent) setSectionProductsLoading(true);
        try {
          const entries = await Promise.all(
            sections.map(async (section) => {
              const limit = sectionFetchLimit(section);
              const sectionParam = sectionKeyToApiParam(section.backendKey);
              const response = await api.get(
                `/products?section=${encodeURIComponent(sectionParam)}&view=card&limit=${limit}&offerMeta=true`
              );
              const raw = extractProductsFromApiResponse(response);
              return [section.backendKey, shapeSectionProductRows(raw)];
            })
          );
          setSectionProductsByKey((prev) => {
            const next = Object.fromEntries(entries);
            // Don't shrink lists the user expanded via View All (extra pages already loaded)
            for (const key of Object.keys(next)) {
              const va = viewAllStateRef.current[key];
              if (va?.active && (prev[key]?.length || 0) > next[key].length) {
                next[key] = prev[key];
              }
            }
            return next;
          });
          lastSectionProductsRefreshRef.current = Date.now();
        } catch (error) {
          console.error('Error fetching section products:', error);
          if (!silent) setSectionProductsByKey({});
        } finally {
          if (!silent) setSectionProductsLoading(false);
        }
      })();

      sectionProductsInflightRef.current = run;
      try {
        await run;
      } finally {
        if (sectionProductsInflightRef.current === run) {
          sectionProductsInflightRef.current = null;
        }
      }
    },
    [sections, sectionFetchLimit, shapeSectionProductRows]
  );

  const loadMoreSectionProducts = useCallback(
    async (section) => {
      const key = section.backendKey;
      const state = viewAllStateRef.current[key];
      if (!state?.active || state.loadingMore || state.hasMore === false) return;

      const limit = sectionFetchLimit(section);
      const nextPage = (state.page || 1) + 1;
      setViewAllState((prev) => ({
        ...prev,
        [key]: { ...prev[key], loadingMore: true }
      }));

      try {
        const sectionParam = sectionKeyToApiParam(key);
        const response = await api.get(
          `/products?section=${encodeURIComponent(sectionParam)}&view=card&limit=${limit}&page=${nextPage}&offerMeta=true`
        );
        const raw = extractProductsFromApiResponse(response);
        const shaped = shapeSectionProductRows(raw);
        const pagination = response?.data?.pagination || response?.pagination;

        setSectionProductsByKey((prev) => {
          const existing = prev[key] || [];
          const seen = new Set(existing.map((p) => String(p._id || p.id)));
          const additions = shaped.filter((p) => !seen.has(String(p._id || p.id)));
          if (!additions.length) return prev;
          return { ...prev, [key]: existing.concat(additions) };
        });

        const hasMore = pagination
          ? nextPage < pagination.pages
          : raw.length >= limit;
        setViewAllState((prev) => ({
          ...prev,
          [key]: { ...prev[key], loadingMore: false, page: nextPage, hasMore }
        }));
      } catch (error) {
        console.error('Error loading more section products:', error);
        // Leave page unchanged so scrolling retries this chunk
        setViewAllState((prev) => ({
          ...prev,
          [key]: { ...prev[key], loadingMore: false }
        }));
      }
    },
    [sectionFetchLimit, shapeSectionProductRows]
  );

  const handleViewAll = (section) => {
    const key = section.backendKey;
    if (viewAllState[key]?.active) {
      // Collapse back to the default rows
      setViewAllState((prev) => ({
        ...prev,
        [key]: { ...prev[key], active: false }
      }));
      setExpandedSections((prev) => {
        const next = new Set(prev);
        next.delete(section.id);
        return next;
      });
      const total = getProductsBySection(section.id, key).length;
      setDisplayCounts((prev) => ({
        ...prev,
        [section.id]: defaultVisibleCount(section, sectionsConfig, total)
      }));
      document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      setViewAllState((prev) => ({
        ...prev,
        [key]: {
          active: true,
          page: prev[key]?.page || 1,
          hasMore: prev[key]?.hasMore ?? true,
          loadingMore: false
        }
      }));
    }
  };

  useEffect(() => {
    loadSectionProducts({ force: true });
  }, [loadSectionProducts]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadSectionProducts({ silent: true });
    }, SECTION_PRODUCTS_REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadSectionProducts]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadSectionProducts({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadSectionProducts]);

  useEffect(() => {
    if (!lastFetch || sections.length === 0) return;
    loadSectionProducts({ silent: true });
  }, [lastFetch, sections.length, loadSectionProducts]);

  const browsableCatalog = useMemo(
    () => filterBrowsableProducts(products),
    [products]
  );

  const browsableIdSet = useMemo(
    () => buildBrowsableProductIdSet(browsableCatalog),
    [browsableCatalog]
  );

  /** Section rows are valid from the section API — include their ids even if not yet in the main catalog load. */
  const sectionCatalogIdSet = useMemo(() => {
    const set = new Set(browsableIdSet);
    Object.values(sectionProductsByKey).flat().forEach((p) => {
      if (p?.id != null) set.add(String(p.id));
      if (p?._id != null) set.add(String(p._id));
    });
    return set;
  }, [browsableIdSet, sectionProductsByKey]);

  const getProductsBySection = useCallback((sectionId, backendKey) => {
    const key = backendKey || sectionKeyMap[sectionId] || sectionId;
    return sectionProductsByKey[key] || [];
  }, [sectionProductsByKey]);

  // Seed default visible count from admin display count (skip sections user expanded via Show More).
  useEffect(() => {
    if (sectionProductsLoading || sections.length === 0) return;

    setDisplayCounts((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const section of sections) {
        if (viewAllStateRef.current[section.backendKey]?.active) continue;
        if (expandedSections.has(section.id)) continue;
        const total = getProductsBySection(section.id, section.backendKey).length;
        const v = defaultVisibleCount(section, sectionsConfig, total);
        if (next[section.id] == null) {
          next[section.id] = v;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [
    windowWidth,
    sectionsConfig,
    sections,
    expandedSections,
    sectionProductsLoading,
    getProductsBySection,
  ]);

  const handleShowMore = (section, groupedTotal) => {
    if (!section) return;
    const columns = getColumnsForSection(section, windowWidth, sectionsConfig);
    const rowBatch = columns * 2;
    setExpandedSections((prev) => new Set(prev).add(section.id));
    setDisplayCounts((prev) => {
      const cur =
        prev[section.id] ?? defaultVisibleCount(section, sectionsConfig, groupedTotal);
      return {
        ...prev,
        [section.id]: Math.min(groupedTotal, cur + rowBatch),
      };
    });
  };

  // Skeleton only until the first section product payload arrives — never gate on full catalog,
  // and never re-skeleton on silent config/product refreshes once cards are on screen.
  const hasPaintedSectionProducts = Object.values(sectionProductsByKey).some(
    (rows) => Array.isArray(rows) && rows.length > 0
  );
  const showSkeleton =
    !hasPaintedSectionProducts && (sectionsLoading || sectionProductsLoading);
  const skeletonSectionCount = 3;
  const skeletonGridColumns = computeSectionGridColumns(windowWidth);
  const skeletonCardsPerSection = Math.min(12, Math.max(4, skeletonGridColumns * 2));

  if (showSkeleton) {
    const titles = sections.length > 0
      ? sections.slice(0, skeletonSectionCount).map(s => s.title)
      : ['Best Sellers', 'Weekly Promotion', 'Populars'];
    return (
      <div className="product-sections">
        {titles.map((title, sectionIndex) => (
          <section key={sectionIndex} className="product-section product-section--skeleton">
            <div className="section-container">
              <div className="section-header">
                <div className="section-skeleton-title" />
                <div className="section-skeleton-link" />
              </div>
              <div className="section-products">
                {Array.from({ length: skeletonCardsPerSection }).map((_, i) => (
                  <div key={i} className="product-card-skeleton">
                    <div className="product-card-skeleton-image" />
                    <div className="product-card-skeleton-line product-card-skeleton-line--wide" />
                    <div className="product-card-skeleton-line" />
                    <div className="product-card-skeleton-line product-card-skeleton-line--narrow" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        ))}
      </div>
    );
  }

  // Don't render if no active sections
  if (sections.length === 0) {
    return null;
  }

  return (
    <div className="product-sections">
      {sections.map(section => {
        const productsForSection = getProductsBySection(section.id, section.backendKey);
        const catalogSource = browsableCatalog.length ? browsableCatalog : productsForSection;
        const groupedSectionProducts = groupBrowsableProductsForGrid(
          productsForSection,
          catalogSource
        ).filter((product) => isProductOpenableInCatalog(product, sectionCatalogIdSet));
        const sectionColumns = getColumnsForSection(section, windowWidth, sectionsConfig);
        const sectionViewAll = viewAllState[section.backendKey] || {};
        const isViewAll = !!sectionViewAll.active;
        const currentCount = isViewAll
          ? groupedSectionProducts.length
          : displayCounts[section.id] ??
            defaultVisibleCount(section, sectionsConfig, groupedSectionProducts.length);
        const visibleProducts = groupedSectionProducts.slice(0, currentCount);
        const hasMore = !isViewAll && groupedSectionProducts.length > currentCount;
        const productsGridClass = sectionUsesFixedColumns(section, windowWidth, sectionsConfig)
          ? `section-products section-products--cols-${sectionColumns}`
          : 'section-products';

        return (
          <section key={section.id} className="product-section" id={section.id}>
            <div className="section-container">
              <div className="section-header">
                <h2 className="section-title">
                  {section.title}
                </h2>
                <button
                  type="button"
                  className="view-all-link"
                  onClick={() => handleViewAll(section)}
                >
                  {isViewAll ? 'Show Less ↑' : 'View All →'}
                </button>
              </div>
              {visibleProducts.length > 0 ? (
                <>
                  <div className={productsGridClass}>
                    {visibleProducts.map((product, index) => (
                      <ProductCard 
                        key={product.offerGroup?.key || product.id || product._id} 
                        product={product} 
                        eagerImage={index < sectionColumns}
                        onAddToList={handleAddToList}
                        onAddToCompare={onAddToCompare}
                        onCheckAvailability={onCheckAvailability}
                        onRemoveFromList={onRemoveFromList}
                        listProductIds={listProductIds}
                        compareProductIds={compareProductIds}
                      />
                    ))}
                  </div>
                  {hasMore && (
                    <div className="section-show-more">
                      <button 
                        className="section-show-more-btn"
                        onClick={() => handleShowMore(section, groupedSectionProducts.length)}
                      >
                        Show More
                      </button>
                    </div>
                  )}
                  {isViewAll && sectionViewAll.hasMore !== false && (
                    <div className="section-show-more">
                      <button
                        className="section-show-more-btn"
                        onClick={() => loadMoreSectionProducts(section)}
                        disabled={!!sectionViewAll.loadingMore}
                      >
                        {sectionViewAll.loadingMore ? 'Loading…' : 'Show More'}
                      </button>
                    </div>
                  )}
                  {isViewAll && (
                    <SectionInfiniteLoader
                      hasMore={sectionViewAll.hasMore !== false}
                      loading={!!sectionViewAll.loadingMore}
                      onLoadMore={() => loadMoreSectionProducts(section)}
                    />
                  )}
                </>
              ) : (
                <div className="no-products">
                  <p>No products available in this section.</p>
                </div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default ProductSections;

